import { useState, useEffect, useRef } from "react";
import { mitSitzungspruefung } from "../utils/fetchWithAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LiveErgebnisse() {
  const [rawResults, setRawResults] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const saved = localStorage.getItem("autoRefreshEnabled");
    return saved ? JSON.parse(saved) : true; // Standard: an
  });
  const intervalRef = useRef(null);

  // Stations einmalig laden
  useEffect(() => {
    const fetchStations = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/stations`, {
          headers: { Authorization: `Bearer ${token}` },
        }));
        if (!res.ok) throw new Error("Fehler beim Laden der Stationen");
        const data = await res.json();
        setStations(data);
      } catch (err) {
        console.error("Fehler beim Laden der Stationen:", err);
      }
    };
    fetchStations();
  }, []);

  // Nur Roh-Ergebnisse laden.
  //
  // GEAENDERT: frueher `/entries` – also die VOLLSTAENDIGE raw_results-Collection,
  // die hier anschliessend auf zehn Eintraege zugeschnitten wurde. Am
  // Veranstaltungstag sind das rund 650 Dokumente pro Abruf, alle zwei Minuten,
  // pro geoeffnetem Tab. `/entries/live` liefert serverseitig sortiert die
  // letzten zwoelf und beantwortet den Abruf aus dem Cache, solange niemand
  // schreibt. Die Route ist admin-only – diese Seite ebenfalls (App.js).
  const fetchRawResults = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = mitSitzungspruefung(await fetch(`${BACKEND_URL}/entries/live`, {
        headers: { Authorization: `Bearer ${token}` },
      }));
      if (!res.ok) throw new Error("Fehler beim Laden der Roh-Ergebnisse");
      const data = await res.json();
      // Sortierung und Zuschnitt bleiben als Absicherung stehen: die Route
      // liefert bereits sortiert, aber die Anzeige soll nicht davon abhaengen.
      const sorted = data
        .map((r) => ({
          ...r,
          timestamp: r.timestamp ? new Date(r.timestamp) : new Date(),
        }))
        .sort((a, b) => b.timestamp - a.timestamp)
        .slice(0, 10);
      setRawResults(sorted);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Fehler beim Laden der Letzten Ergebnisse:", err);
    } finally {
      setLoading(false);
    }
  };

  // Interval-Handling mit Sichtbarkeit + AutoRefresh
  useEffect(() => {
    // Einmal beim Mounten laden – unabhaengig davon, ob Auto-Refresh an ist.
    // (Frueher stand derselbe Aufruf zusaetzlich im autoRefresh-Zweig unten:
    // bei aktivem Auto-Refresh, dem Standard, wurde beim Oeffnen der Seite
    // doppelt geladen.)
    fetchRawResults();

    const startInterval = () => {
      if (intervalRef.current) return; // schon aktiv
      intervalRef.current = setInterval(fetchRawResults, 120000);
    };

    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && autoRefresh) {
        fetchRawResults(); // sofort laden
        startInterval();
      } else {
        stopInterval();
      }
    };

    if (autoRefresh) {
      startInterval();
      document.addEventListener("visibilitychange", handleVisibility);
    } else {
      stopInterval();
    }

    return () => {
      stopInterval();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [autoRefresh]);

  // Toggle speichern
  const toggleAutoRefresh = () => {
    const newValue = !autoRefresh;
    setAutoRefresh(newValue);
    localStorage.setItem("autoRefreshEnabled", JSON.stringify(newValue));
  };

  const getStation = (station_number) =>
    stations.find((s) => s.station_number === station_number) || {};

  return (
    <div className="p-4 bg-secondary-950 min-h-screen text-white">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-primary-500">Letzten Ergebnisse</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchRawResults}
            className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded"
          >
            {loading ? "Lade..." : "Aktualisieren"}
          </button>
          <button
            onClick={toggleAutoRefresh}
            className={`px-4 py-2 rounded ${
              autoRefresh
                ? "bg-green-600 hover:bg-green-700"
                : "bg-gray-600 hover:bg-gray-700"
            }`}
          >
            {autoRefresh ? "Auto-Refresh an" : "Auto-Refresh aus"}
          </button>
        </div>
      </div>

      {lastUpdated && (
        <p className="text-gray-400 text-sm mb-4">
          Zuletzt aktualisiert:{" "}
          {lastUpdated.toLocaleTimeString("de-DE", {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          })}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading
          ? [...Array(5)].map((_, i) => (
              <div
                key={i}
                className="p-4 bg-secondary-900 rounded shadow animate-pulse h-32"
              />
            ))
          : rawResults.map((r, idx) => {
              const station = getStation(r.station_number);
              const unit = station.unit ? ` ${station.unit}` : "";
              return (
                <div
                  key={idx}
                  className="p-4 bg-secondary-900 rounded shadow hover:shadow-lg transition-shadow cursor-pointer flex flex-col justify-between"
                >
                  <div>
                    {/* `team_name` ist der denormalisierte Anzeigename am
                        Ergebnis; `team` deckt Eintraege von vor der
                        ID-Umstellung ab. Diese Ansicht laedt bewusst keine
                        Team-Liste, deshalb steht der Name am Eintrag selbst. */}
                    <p className="font-bold text-lg truncate">{r.team_name ?? r.team}</p>
                    <p className="text-gray-400 text-sm truncate">
                      {station.station_name || r.station_number}{" "}
                      <span className="text-gray-500">#{r.station_number}</span>
                    </p>
                  </div>

                  <div className="mt-2 flex justify-between items-center">
                    <p className="text-2xl font-extrabold text-primary-500">
                      {r.performance_score ?? r.points}
                      {unit}
                    </p>
                    <p className="text-gray-400 text-xs text-right">
                      {r.timestamp.toLocaleTimeString("de-DE", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                      <br />
                      {r.entry_by}
                    </p>
                  </div>
                </div>
              );
            })}
      </div>

      {rawResults.length === 0 && !loading && (
        <p className="mt-4 text-gray-400 text-center">
          Keine Ergebnisse vorhanden.
        </p>
      )}
    </div>
  );
}
